import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { ProviderSidebarComponent } from '../sidebar/provider-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { MyProjectComponent } from '../../shared/components/my-project/my-project.component';
import { CanComponentDeactivate } from '../../guards/my-projects-deactivate.guard';
import { ModalComponent } from '../../shared/components/primeng/modal/modal.component';
import { AuthApiService } from '@angular-workspace/services';
import { Observable } from 'rxjs';

export type MyProjectsAccessLevel = 'loading' | 'none' | 'pending' | 'full';

@Component({
  selector: 'app-provider-my-projects',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    ProviderSidebarComponent,
    PageHeaderComponent,
    MyProjectComponent,
    ModalComponent,
  ],
  templateUrl: './my-projects.component.html',
  styleUrl: './my-projects.component.scss',
})
export class ProviderMyProjectsComponent
  implements CanComponentDeactivate, OnInit
{
  @ViewChild(MyProjectComponent) myProjectComponent!: MyProjectComponent;

  accessLevel: MyProjectsAccessLevel = 'loading';
  accessModalDismissed = false;

  get showAccessModal(): boolean {
    return (
      (this.accessLevel === 'none' || this.accessLevel === 'pending') &&
      !this.accessModalDismissed
    );
  }

  constructor(
    private authApiService: AuthApiService,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.checkComputeAccess();
  }

  private async checkComputeAccess(): Promise<void> {
    try {
      const userData = await this.authApiService.getUserInfo();
      const actual = userData?.['result'] ?? userData;
      const roles: string[] = actual?.roles ?? [];
      const pendingRoles: string[] = actual?.pendingRoles ?? [];

      const hasActiveCompute = roles.some(
        (role: string) => role.toLowerCase() === 'compute',
      );
      const hasPendingCompute = pendingRoles.some(
        (role: string) => role.toLowerCase() === 'compute',
      );

      if (hasActiveCompute) {
        this.accessLevel = 'full';
      } else if (hasPendingCompute) {
        this.accessLevel = 'pending';
      } else {
        this.accessLevel = 'none';
      }
    } catch (error) {
      console.error('Error checking Compute role for My Projects:', error);
      this.accessLevel = 'none';
    }
  }

  onAccessModalClose(): void {
    this.router.navigate(['/provider/home']);
  }

  onRoleUpgradeClick(): void {
    this.accessModalDismissed = true;
    const event = new CustomEvent('openUpgradeModal', {
      detail: { source: 'my-projects' },
    });
    window.dispatchEvent(event);
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.myProjectComponent) {
      return this.myProjectComponent.canDeactivate();
    }
    return true;
  }
}
